import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Instagram, Linkedin, Youtube } from "lucide-react";
import { api, SOCIALS } from "@/lib/api";

const BehanceIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22 7h-7V5h7v2zM5.5 6c1.93 0 3.5 1.34 3.5 3 0 .73-.3 1.39-.78 1.91 1.32.54 2.28 1.81 2.28 3.34 0 2.01-1.79 3.75-4 3.75H0V6h5.5zm-.5 4.5c.83 0 1.5-.67 1.5-1.5S5.83 7.5 5 7.5H2v3h3zM5 16c.83 0 1.5-.67 1.5-1.5S5.83 13 5 13H2v3h3zM19 9c2.21 0 4 2.24 4 5 0 .35-.04.69-.1 1H16c.4 1.16 1.4 2 2.5 2 .76 0 1.45-.4 1.85-1H22.3c-.6 1.75-2.4 3-4.3 3-2.5 0-4.5-2.24-4.5-5s2-5 4.5-5h1zM16 13h5c-.3-1.16-1.3-2-2.5-2-1.1 0-2.1.84-2.5 2z"/>
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", brand: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", brand: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at hello@styleepix.co");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-40 bg-[#0c1a14]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[11px] uppercase tracking-[0.4em] text-gold">Let&apos;s Talk</span>
              <span className="h-px w-16 bg-[#264738]" />
            </div>
            <h2 className="font-display text-ivory text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-10">
              Have a brand <br/>worth filming? <span className="italic text-gold">Let&apos;s create.</span>
            </h2>
            <p className="text-ivory-muted text-base font-light leading-relaxed mb-12 max-w-md">
              Tell us about your brand and what you&apos;d like to make. We respond within 24 hours.
            </p>

            <div className="space-y-5">
              <a
                href={`mailto:${SOCIALS.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email-link"
              >
                <Mail className="text-gold" size={18} />
                <span className="gold-link text-ivory text-base">{SOCIALS.email}</span>
              </a>

              <div className="hairline my-8" />

              <div className="flex items-center gap-6">
                <span className="text-[10px] uppercase tracking-[0.4em] text-ivory-muted">Follow</span>
                <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="text-ivory-muted hover:text-gold transition-colors" aria-label="Instagram" data-testid="social-instagram">
                  <Instagram size={18} />
                </a>
                <a href={SOCIALS.behance} target="_blank" rel="noreferrer" className="text-ivory-muted hover:text-gold transition-colors" aria-label="Behance" data-testid="social-behance">
                  <BehanceIcon width="20" height="20" />
                </a>
                <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="text-ivory-muted hover:text-gold transition-colors" aria-label="LinkedIn" data-testid="social-linkedin">
                  <Linkedin size={18} />
                </a>
                <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" className="text-ivory-muted hover:text-gold transition-colors" aria-label="YouTube" data-testid="social-youtube">
                  <Youtube size={18} />
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            onSubmit={onSubmit}
            className="lg:col-span-7 space-y-8"
            data-testid="contact-form"
          >
            <Field
              label="Your Name"
              value={form.name}
              onChange={onChange("name")}
              placeholder="Jane Doe"
              testid="contact-name"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="jane@brand.co"
                testid="contact-email"
              />
              <Field
                label="Brand / Company (optional)"
                value={form.brand}
                onChange={onChange("brand")}
                placeholder="Your Brand"
                testid="contact-brand"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">
                Tell us about your project
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Briefly: scope, timeline, references…"
                className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold transition-colors py-3 text-ivory placeholder:text-ivory-muted/40 outline-none resize-none font-light"
                data-testid="contact-message"
              />
            </div>

            <div className="flex items-center justify-between pt-4">
              <p className="text-[11px] uppercase tracking-[0.3em] text-ivory-muted">
                {submitted ? "Inquiry received." : "We reply within 24 hours."}
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 bg-gold text-forest hover:bg-gold-400 px-8 py-4 text-[11px] uppercase tracking-[0.35em] transition-colors disabled:opacity-50"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send Inquiry"}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.4em] text-ivory-muted mb-3">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b border-[#264738] focus:border-gold transition-colors py-3 text-ivory placeholder:text-ivory-muted/40 outline-none font-light"
        data-testid={testid}
      />
    </div>
  );
}
